import { FederatedAuthentication } from '@nimbly-technologies/nimbly-common';
import API, { gatewayFetch } from 'helpers/api';
import { apiURL } from 'config/baseURL';
import { auth } from 'store/configStore';
import { authenticateWithCredentialStore } from 'components/landing/utils/authenticateCredentials';

const UNKNOWN_ERROR = {
	status: 'unknown_error',
	errorMsg: 'Unknown error',
	errorField: '',
	data: null,
};

export const authenticateFederatedCredentials = (tenant: FederatedAuthentication, firebase: any): Promise<any> => {
	const newAuth = auth();
	newAuth.tenantId = tenant.tenantID;
	const provider = new firebase.auth.OAuthProvider(tenant.loginProviderID);

	return newAuth
		.signInWithPopup(provider)
		.then((result: any) => {
			if (result) return API.getFirebaseToken();
		})
		.then((res: any) => {
			return res;
		})
		.catch((error: any) => {
			return {
				pendingCred: error.credential,
				errorCode: error.code,
			};
		});
};

export const linkAccount = async (email: string, password: string, pendingCredential: any): Promise<any> => {
	const authentication = auth();
	try {
		if (!password) {
			return {
				status: 'field_not_complete',
				errorMsg: 'One or more fields are missing.',
				errorField: '',
			};
		}
		const signInMethods = await authentication.fetchSignInMethodsForEmail(email);
		if (signInMethods[0] === 'password') {
			const result = await authenticateWithCredentialStore(email, password, authentication);
			if (result.status !== 'ok') return result;
			await authentication.currentUser?.linkWithCredential(pendingCredential);
			return result;
		}
		return UNKNOWN_ERROR;
	} catch {
		return UNKNOWN_ERROR;
	}
};

export const authenticateMultiTenant = async (
	tenant: FederatedAuthentication,
	email: string,
	password: string,
): Promise<any> => {
	try {
		if (!password) {
			return {
				status: 'field_not_complete',
				errorMsg: 'One or more fields are missing.',
				errorField: '',
			};
		}
		if (tenant?.tenantID) {
			// api-users currently mints project-level custom tokens. Production
			// has no non-empty tenant IDs; fail closed if one is added instead of
			// silently restoring Firebase password verification.
			return UNKNOWN_ERROR;
		}
		return authenticateWithCredentialStore(email, password, auth());
	} catch {
		return UNKNOWN_ERROR;
	}
};

export const findTenantByEmailDomain = async (emailDomain: string, email: string) => {
	try {
		const url = `${apiURL}/auth/federated/domain`;

		const bodySend = {
			domain: emailDomain,
			email: email,
		};

		const options = {
			method: 'POST',
			headers: {
				'Content-Type': 'application/json',
			},
			body: JSON.stringify(bodySend),
		};
		const fetchData = await gatewayFetch(url, options);
		if (fetchData.status !== 200) return false;

		const result = await fetchData.json();
		return result.data;
	} catch (error) {
		return error;
	}
};

export const checkUserEnrollment = async (idToken: string, tenantID: string) => {
	try {
		const url = `${apiURL}/auth/federated/verify-enrollment`;
		const bodySend = {
			token: idToken,
			tenantID,
		};

		const options = {
			method: 'POST',
			headers: {
				'Content-Type': 'application/json',
			},
			body: JSON.stringify(bodySend),
		};

		const fetching = await gatewayFetch(url, options);
		if (fetching.status !== 200) {
			const res = await fetching.json();
			return res;
		}

		const result = await fetching.json();
		return result;
	} catch (error) {
		return error;
	}
};

export const requestAccessEnrollment = async (token: string, tenant: FederatedAuthentication) => {
	try {
		const url = `${apiURL}/auth/user-enrollment`;
		const body = {
			token: token,
			tenant: tenant,
		};

		const options = {
			method: 'POST',
			headers: {
				'Content-Type': 'application/json',
			},
			body: JSON.stringify(body),
		};

		const requesting = await gatewayFetch(url, options);
		if (requesting.status !== 200) return false;

		const response = await requesting.json();
		return response.data;
	} catch (error) {
		return error;
	}
};
